import type { GeoLocation, RadiusOption } from "@repo/design-system/types/geo";
import type {
  CommonJobLocation,
  JobMapPin,
} from "@repo/design-system/types/job";
import { getJobAdUrlFromData } from "@repo/design-system/utils/helpers";

export interface MapCenter {
  center: [number, number];
  zoom: number;
}

export interface MapMarker {
  description: string;
  id: string;
  position: [number, number];
  title: string;
  url: string;
}

function getZoomForRadius(radius: number) {
  switch (radius) {
    case 5000:
      return 12;
    case 25_000:
      return 9.5;
    case 50_000:
      return 8.5;
    case 100_000:
      return 7.5;
    case 300_000:
      return 6;
    default:
      return 9.5;
  }
}

function getZoomForBboxSize(bboxSize: number): number {
  if (bboxSize > 5_000_000) {
    return 3.5;
  }
  if (bboxSize > 1_500_000) {
    return 4;
  }
  if (bboxSize > 750_000) {
    return 5;
  }
  if (bboxSize > 500_000) {
    return 6;
  }
  if (bboxSize > 350_000) {
    return 6.5;
  }
  if (bboxSize > 250_000) {
    return 7;
  }
  if (bboxSize > 150_000) {
    return 7.5;
  }
  if (bboxSize > 100_000) {
    return 8;
  }
  if (bboxSize > 50_000) {
    return 8.5;
  }
  if (bboxSize > 20_000) {
    return 9.5;
  }
  if (bboxSize > 5000) {
    return 10;
  }

  return 5;
}

export function getCenterAndZoomForGeoLocation({
  geoLocation,
  bboxSize,
  radius,
}: {
  geoLocation: GeoLocation;
  radius: RadiusOption;
  bboxSize: number | undefined;
}): MapCenter {
  let zoom: number;
  if (radius.value === 0) {
    if (bboxSize) {
      zoom = getZoomForBboxSize(bboxSize);
    } else {
      zoom = 5;
    }
  } else {
    zoom = getZoomForRadius(radius.value);
  }

  return {
    center: [geoLocation.lat, geoLocation.lng],
    zoom,
  };
}

export function getCenterAndZoomForLocale(locale?: string): MapCenter {
  switch (locale) {
    case "de":
      return { center: [51, 10], zoom: 6 };
    case "en":
      return { center: [41.902, -57.48], zoom: 3 };
    case "es":
      return { center: [5.4052, -3.713], zoom: 2 };
    case "fr":
      return { center: [45.435, -40.479], zoom: 3 };
    case "zh":
      return { center: [28.822, 109.267], zoom: 3 };
    case "cn":
      return { center: [35.022, 100.412], zoom: 3.5 };
    case "hu":
      return { center: [47.075, 19.164], zoom: 5.5 };
    case "ro":
      return { center: [45.9, 24.9], zoom: 5.5 };
    case "sv":
      return { center: [58, 15], zoom: 4.5 };
    case "cs":
      return { center: [49.75, 15.5], zoom: 6 };
    case "it":
      return { center: [42.5, 12.5], zoom: 5.5 };
    case "pt":
      return { center: [-12.443, -35.221], zoom: 3 };
    default:
      return { center: [51.296, 10.327], zoom: 5.8 };
  }
}

const defaultFormatDescription = (
  location: CommonJobLocation,
  company: string
) => `${location.city}, ${location.state}, ${location.country} - ${company}`;

export function getMarkersForJobs(
  jobPins?: readonly JobMapPin[],
  formatDescription: (
    location: CommonJobLocation,
    company: string
  ) => string = defaultFormatDescription
) {
  const markers: MapMarker[] = [];

  for (const jobPin of jobPins ?? []) {
    const geolocs = getGeoLoc(jobPin);

    for (let index = 0; index < geolocs.length; index++) {
      const geoloc = geolocs[index];
      const location = jobPin.data.locations[index] || jobPin.data.locations[0];

      markers.push({
        id: `${jobPin.data.idClient}_${index}`,
        title: jobPin.data.title,
        description: formatDescription(location, jobPin.data.company),
        position: [geoloc.lat, geoloc.lng],
        url: getJobAdUrlFromData(jobPin.data),
      });
    }
  }

  return markers;
}

function getGeoLoc(jobPin: JobMapPin) {
  if (Array.isArray(jobPin._geoloc)) {
    return jobPin._geoloc;
  }

  if (jobPin._geoloc) {
    return [jobPin._geoloc];
  }

  return [];
}
